/**
 * Gmail Token Refresh Service
 *
 * Proactively refreshes Gmail access tokens in the background
 * to prevent token expiration errors during sync operations
 */

import { supabase } from './supabaseClient';
import { getGmailAccessToken } from './gmailOAuthService';

// Store refresh intervals
const refreshIntervals = new Map();

/**
 * Start automatic token refresh for all Gmail accounts
 * Refreshes tokens every 50 minutes (before 1 hour expiration)
 * @returns {Promise<void>}
 */
export const startTokenRefreshService = async () => {
  try {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔄 Starting Gmail Token Refresh Service');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // Get all Gmail accounts for current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.log('⚠️ No authenticated user, skipping token refresh service');
      return;
    }

    console.log(`👤 User ID: ${user.id}`);

    const { data: accounts, error } = await supabase
      .from('synced_gmail_accounts')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('❌ Error fetching Gmail accounts:', error);
      return;
    }

    if (!accounts || accounts.length === 0) {
      console.log('ℹ️ No Gmail accounts found, skipping token refresh service');
      console.log('   (User needs to connect a Gmail account first)');
      return;
    }

    console.log(`✅ Found ${accounts.length} Gmail account(s) to monitor:`);
    accounts.forEach((acc, i) => {
      console.log(`   ${i + 1}. ${acc.gmail_email} (Status: ${acc.sync_status})`);
    });

    // Start refresh interval for accounts that have a refresh token
    // (accounts saved during signup may not have one yet — they'll get it during onboarding)
    const accountsWithTokens = accounts.filter(a => a.gmail_refresh_token);
    const skippedCount = accounts.length - accountsWithTokens.length;

    if (skippedCount > 0) {
      console.log(`   ⏭️ Skipping ${skippedCount} account(s) without refresh tokens (pending onboarding)`);
    }

    accountsWithTokens.forEach(account => {
      startRefreshForAccount(account);
    });

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('✅ Token refresh service started successfully');
    console.log('🔄 Tokens will refresh every 50 minutes');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
  } catch (error) {
    console.error('❌ Error starting token refresh service:', error);
  }
};

/**
 * Start token refresh for a specific account
 * @param {Object} account - Gmail account object
 */
const startRefreshForAccount = (account) => {
  // Clear existing interval if any
  if (refreshIntervals.has(account.id)) {
    clearInterval(refreshIntervals.get(account.id));
  }

  console.log(`🔄 Starting token refresh for ${account.gmail_email}`);

  // Refresh immediately on start
  refreshTokenForAccount(account);

  // Then refresh every 50 minutes (3000000ms)
  // Google access tokens expire after 1 hour, so we refresh at 50 mins
  const interval = setInterval(() => {
    refreshTokenForAccount(account);
  }, 50 * 60 * 1000); // 50 minutes

  refreshIntervals.set(account.id, interval);
};

/**
 * Refresh token for a specific account
 * @param {Object} account - Gmail account object
 */
const refreshTokenForAccount = async (account) => {
  // Skip accounts without refresh tokens (saved during signup, pending onboarding)
  if (!account.gmail_refresh_token) {
    return;
  }

  try {
    console.log(`🔄 Refreshing token for ${account.gmail_email}...`);

    // Get new access token using refresh token
    const accessToken = await getGmailAccessToken(account.gmail_refresh_token);

    if (accessToken) {
      console.log(`✅ Token refreshed successfully for ${account.gmail_email}`);

      // Update account status to clear any previous errors
      await supabase
        .from('synced_gmail_accounts')
        .update({
          sync_status: account.sync_status === 'auth_required' ? 'pending' : account.sync_status,
          sync_error: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', account.id);
    }
  } catch (error) {
    console.error(`❌ Failed to refresh token for ${account.gmail_email}:`, error);

    // Check if this is a permanent failure (invalid refresh token)
    const isPermanentError = error.message?.includes('invalid_grant') ||
      error.message?.includes('Token has been expired or revoked');

    if (isPermanentError) {
      console.log(`🛑 Permanent token error for ${account.gmail_email}, stopping auto-refresh`);

      // Stop refreshing this account
      stopRefreshForAccount(account.id);

      // Update account status to require re-authentication
      await supabase
        .from('synced_gmail_accounts')
        .update({
          sync_status: 'auth_required',
          sync_error: 'Token expired or revoked. Please re-authenticate.',
          updated_at: new Date().toISOString()
        })
        .eq('id', account.id);
    }
  }
};

/**
 * Stop token refresh for a specific account
 * @param {string} accountId - Gmail account ID
 */
export const stopRefreshForAccount = (accountId) => {
  if (refreshIntervals.has(accountId)) {
    clearInterval(refreshIntervals.get(accountId));
    refreshIntervals.delete(accountId);
    console.log(`🛑 Stopped token refresh for account ${accountId}`);
  }
};

/**
 * Stop all token refresh intervals
 */
export const stopTokenRefreshService = () => {
  console.log('🛑 Stopping all token refresh intervals');
  refreshIntervals.forEach((interval, accountId) => {
    clearInterval(interval);
    console.log(`   Stopped refresh for account ${accountId}`);
  });
  refreshIntervals.clear();
};

/**
 * Manually trigger token refresh for a specific account
 * Useful after re-authentication
 * @param {string} accountId - Gmail account ID
 */
export const refreshAccountToken = async (accountId) => {
  try {
    const { data: account, error } = await supabase
      .from('synced_gmail_accounts')
      .select('*')
      .eq('id', accountId)
      .single();

    if (error) throw error;

    await refreshTokenForAccount(account);

    // Restart the interval for this account
    startRefreshForAccount(account);
  } catch (error) {
    console.error('Error manually refreshing account token:', error);
    throw error;
  }
};

/**
 * Add a newly connected Gmail account to the refresh service
 * @param {Object} account - Gmail account object
 */
export const addAccountToRefreshService = (account) => {
  console.log(`➕ Adding ${account.gmail_email} to token refresh service`);
  startRefreshForAccount(account);
};

export default {
  startTokenRefreshService,
  stopTokenRefreshService,
  stopRefreshForAccount,
  refreshAccountToken,
  addAccountToRefreshService
};
